/**
 * Component to control EoiHeaderButtons and Navbar to appear as sticky header individually
 */

/** @jsx jsx */

/** @jsxFrag */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import throttle from 'lodash/throttle';

import { breakpoints, useMediaQuery, useTheme } from '@coursera/cds-core';

import Container from 'bundles/degree-description/components/shared/Container';
import EoiHeaderButtons from 'bundles/degree-description/components/shared/Navigation/EoiHeaderButtons';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';

type Props = {
  partnerLogoUrl?: string;
  partnerName?: string;
  programName?: string;
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  eoiHeaderRefElement?: HTMLElement | null;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
};

const StickyHeader: React.FC<Props> = ({
  partnerLogoUrl,
  partnerName,
  programName,
  eoiButtons,
  eoiContainerText,
  eoiHeaderRefElement,
  setEoiTrackingIndex,
}) => {
  const theme = useTheme();
  const isDesktopView = useMediaQuery(breakpoints.up('md'));
  const [isEoiHeaderFixed, setIsEoiHeaderFixed] = useState<boolean>(false);

  /**
   * This useEffect determines how far down the page the user has scrolled
   * and sets whether or not the EoiHeaderButtons are sticky
   * If in desktop view and scrolled past the eoi buttons - EoiHeaderButtons is sticky
   */
  useEffect(() => {
    const onScrollHandler = throttle(() => {
      const refElementBottom = eoiHeaderRefElement?.getBoundingClientRect().bottom ?? 0;
      const scrollYTrack = window.scrollY;
      if (isDesktopView) {
        if (scrollYTrack > 0 && refElementBottom <= 0) {
          setIsEoiHeaderFixed(true);
        } else {
          setIsEoiHeaderFixed(false);
        }
      } else {
        setIsEoiHeaderFixed(false);
      }
    }, 50);

    window.addEventListener('scroll', onScrollHandler);
    return () => {
      window.removeEventListener('scroll', onScrollHandler);
    };
  }, [theme, eoiHeaderRefElement, isDesktopView]);

  return (
    <Container>
      <EoiHeaderButtons
        partnerLogoUrl={partnerLogoUrl}
        partnerName={partnerName}
        programName={programName}
        eoiButtons={eoiButtons}
        eoiContainerText={eoiContainerText}
        isFixed={isEoiHeaderFixed}
        setEoiTrackingIndex={setEoiTrackingIndex}
      />
    </Container>
  );
};

export default StickyHeader;
